import { useEffect, useState } from "react";
import type { RecordingStatus } from "@cuekards/core/recording";

/**
 * The elapsed count of the current take, ticking locally between broadcasts.
 *
 * The host sends a status on transitions only. Pushing one every second so a
 * clock could count would be traffic spent on something each view can work
 * out for itself from the last status it received — and it would wake the
 * overlay's compositor once a second for the whole take.
 *
 * Shared by the control window's transport and the overlay's recording badge,
 * so the two never drift apart by a second.
 */
export function useLiveElapsed(status: RecordingStatus): number {
  const [elapsed, setElapsed] = useState(status.elapsedMs);

  useEffect(() => {
    setElapsed(status.elapsedMs);
    // Only a rolling take advances: paused holds, and idle has nothing to show.
    if (status.phase !== "recording") return;
    const from = Date.now();
    const timer = setInterval(
      () => setElapsed(status.elapsedMs + (Date.now() - from)),
      500,
    );
    return () => clearInterval(timer);
  }, [status]);

  return elapsed;
}
